import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import {
  listPlacementSessions,
  listStudents,
  createStudent,
  bulkCreateStudents,
  deleteStudent,
} from "../api/client";

const PAGE_SIZE = 50;

// Shared styling for native <select> so it matches the Input component.
const selectClasses =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

// ── CSV parsing (client-side) ───────────────────────────────────────────────
// A small RFC-4180-ish parser: handles quoted fields, escaped quotes (""),
// and commas/newlines inside quotes. The server re-validates every row.
function parseCSV(text) {
  const rows = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\r") {
      /* ignore */
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => !(r.length === 1 && r[0].trim() === ""));
}

const normHeader = (s) => String(s).trim().toLowerCase().replace(/[\s_-]+/g, "");

const HEADER_ALIASES = {
  name: ["name", "fullname", "studentname"],
  email: ["email", "emailid", "mail", "emailaddress"],
  rollNumber: ["rollnumber", "rollno", "roll", "rollnum", "registrationnumber", "regno"],
  branch: ["branch", "department", "dept", "stream"],
  cgpa: ["cgpa", "gpa"],
  graduationYear: ["graduationyear", "gradyear", "year", "passingyear", "passoutyear", "batchyear"],
};

function mapHeaders(headerRow) {
  const idx = {};
  headerRow.forEach((h, i) => {
    const n = normHeader(h);
    for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(n)) idx[key] = i;
    }
  });
  return idx;
}

function csvToStudents(text) {
  const rows = parseCSV(text);
  if (rows.length === 0) return { students: [], error: "The file appears to be empty." };
  const idx = mapHeaders(rows[0]);
  if (idx.name == null || idx.email == null) {
    return {
      students: [],
      error: "CSV must include at least 'name' and 'email' columns.",
    };
  }
  const students = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const get = (k) => (idx[k] != null ? String(row[idx[k]] ?? "").trim() : "");
    const name = get("name");
    const email = get("email");
    if (!name && !email) continue; // skip blank lines
    const s = { name, email };
    const roll = get("rollNumber");
    if (roll) s.rollNumber = roll;
    const branch = get("branch");
    if (branch) s.branch = branch;
    const cgpaStr = get("cgpa");
    if (cgpaStr) {
      const v = parseFloat(cgpaStr);
      if (!Number.isNaN(v)) s.cgpa = v;
    }
    const yearStr = get("graduationYear");
    if (yearStr) {
      const v = parseInt(yearStr, 10);
      if (!Number.isNaN(v)) s.graduationYear = v;
    }
    students.push(s);
  }
  return { students, error: students.length === 0 ? "No data rows found under the header." : "" };
}

// ── UI bits ─────────────────────────────────────────────────────────────────

function InvitePill({ status }) {
  const map = {
    pending: "bg-muted text-muted-foreground",
    invited: "bg-warning/15 text-warning",
    registered: "bg-success/15 text-success",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize " +
        (map[status] || "bg-muted text-muted-foreground")
      }
    >
      {status || "pending"}
    </span>
  );
}

const EMPTY_STUDENT = {
  placementSessionId: "",
  name: "",
  email: "",
  rollNumber: "",
  branch: "",
  cgpa: "",
  graduationYear: "",
};

export default function Students() {
  const location = useLocation();
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [batchFilter, setBatchFilter] = useState(
    () => new URLSearchParams(location.search).get("batch") || ""
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);

  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Add-one modal
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_STUDENT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Import modal
  const [importOpen, setImportOpen] = useState(false);
  const [importBatch, setImportBatch] = useState("");
  const [parsed, setParsed] = useState([]);
  const [parseError, setParseError] = useState("");
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Delete confirm
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadBatches = useCallback(async () => {
    try {
      const rows = await listPlacementSessions();
      setBatches(rows);
    } catch {
      /* batch dropdown just stays empty; students load has its own error */
    }
  }, []);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (batchFilter) params.placementSessionId = batchFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const data = await listStudents(params);
      setStudents(data.students || []);
      setTotal(data.total || 0);
    } catch (err) {
      setLoadError(err?.error || "Could not load students.");
    } finally {
      setLoading(false);
    }
  }, [batchFilter, debouncedSearch, page]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const hasBatches = batches.length > 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const batchName = useMemo(() => {
    if (!batchFilter) return "";
    return batches.find((b) => b.id === batchFilter)?.name || "";
  }, [batchFilter, batches]);

  // ── Add one ────────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm({ ...EMPTY_STUDENT, placementSessionId: batchFilter || (batches[0]?.id ?? "") });
    setFormError("");
    setAddOpen(true);
  };
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submitAdd = async (e) => {
    e?.preventDefault?.();
    setFormError("");
    if (!form.placementSessionId) return setFormError("Choose a batch.");
    if (!form.name.trim()) return setFormError("Name is required.");
    if (!form.email.trim()) return setFormError("Email is required.");
    const payload = {
      placementSessionId: form.placementSessionId,
      name: form.name.trim(),
      email: form.email.trim(),
    };
    if (form.rollNumber.trim()) payload.rollNumber = form.rollNumber.trim();
    if (form.branch.trim()) payload.branch = form.branch.trim();
    if (form.cgpa !== "") {
      const v = parseFloat(form.cgpa);
      if (Number.isNaN(v) || v < 0 || v > 10) return setFormError("CGPA must be between 0 and 10.");
      payload.cgpa = v;
    }
    if (form.graduationYear !== "") {
      const v = parseInt(form.graduationYear, 10);
      if (Number.isNaN(v) || v < 1950 || v > 2100) return setFormError("Graduation year looks invalid.");
      payload.graduationYear = v;
    }
    setSaving(true);
    try {
      await createStudent(payload);
      setAddOpen(false);
      await loadStudents();
    } catch (err) {
      setFormError(err?.error || "Could not add the student.");
    } finally {
      setSaving(false);
    }
  };

  // ── Import ─────────────────────────────────────────────────────────────
  const openImport = () => {
    setImportBatch(batchFilter || (batches[0]?.id ?? ""));
    setParsed([]);
    setParseError("");
    setFileName("");
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setImportOpen(true);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const { students: rows, error } = csvToStudents(String(reader.result || ""));
      setParsed(rows);
      setParseError(error || "");
    };
    reader.onerror = () => setParseError("Could not read the file.");
    reader.readAsText(file);
  };

  const submitImport = async () => {
    if (!importBatch) return setParseError("Choose a batch to import into.");
    if (parsed.length === 0) return setParseError("No valid rows to import.");
    setImporting(true);
    setParseError("");
    try {
      const result = await bulkCreateStudents({
        placementSessionId: importBatch,
        students: parsed,
      });
      setImportResult(result);
      await loadStudents();
    } catch (err) {
      setParseError(err?.error || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteStudent(toDelete.id);
      setToDelete(null);
      // If we just removed the last row on a page, step back a page.
      if (students.length === 1 && page > 0) setPage((p) => p - 1);
      else await loadStudents();
    } catch (err) {
      setLoadError(err?.error || "Could not delete the student.");
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const clearBatchFilter = () => {
    setBatchFilter("");
    setPage(0);
    navigate("/institution/students", { replace: true });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Students</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {batchName ? (
              <span className="inline-flex items-center gap-2">
                Showing <span className="font-medium text-foreground">{batchName}</span>
                <button onClick={clearBatchFilter} className="text-primary underline">
                  clear
                </button>
              </span>
            ) : (
              "Manage your placement roster across all batches."
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" iconName="Upload" onClick={openImport} disabled={!hasBatches}>
            Import CSV
          </Button>
          <Button iconName="UserPlus" onClick={openAdd} disabled={!hasBatches}>
            Add student
          </Button>
        </div>
      </div>

      {/* No batches yet → must create one first */}
      {!hasBatches && !loading && (
        <div className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground">
          <Icon name="Info" size={16} className="text-warning" />
          <span>You need a batch before adding students.</span>
          <button
            onClick={() => navigate("/institution/batches")}
            className="ml-auto font-medium text-primary underline"
          >
            Create a batch
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Icon name="Search" size={16} />
          </span>
          <input
            type="text"
            placeholder="Search by name, email or roll number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
        <select
          value={batchFilter}
          onChange={(e) => {
            setBatchFilter(e.target.value);
            setPage(0);
          }}
          className={selectClasses + " sm:w-64"}
        >
          <option value="">All batches</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {/* Load error */}
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={loadStudents} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Roll no.</th>
                <th className="px-4 py-3 font-medium">Branch</th>
                <th className="px-4 py-3 font-medium">CGPA</th>
                <th className="px-4 py-3 font-medium">Grad yr</th>
                {!batchFilter && <th className="px-4 py-3 font-medium">Batch</th>}
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-4 py-3" colSpan={batchFilter ? 7 : 8}>
                      <div className="h-5 w-full animate-pulse rounded bg-muted/50" />
                    </td>
                  </tr>
                ))
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan={batchFilter ? 7 : 8} className="px-4 py-14 text-center">
                    <div className="flex flex-col items-center">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Icon name="Users" size={22} />
                      </span>
                      <p className="mt-3 text-sm font-medium text-foreground">
                        {debouncedSearch || batchFilter ? "No matching students" : "No students yet"}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {hasBatches
                          ? "Add a student or import a CSV roster to get started."
                          : "Create a batch first, then add students."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                students.map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.email}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{s.rollNumber || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.branch || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {s.cgpa != null ? Number(s.cgpa).toFixed(2) : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{s.graduationYear || "—"}</td>
                    {!batchFilter && (
                      <td className="px-4 py-3 text-muted-foreground">{s.batchName || "—"}</td>
                    )}
                    <td className="px-4 py-3">
                      <InvitePill status={s.inviteStatus} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/institution/students/${s.id}`)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                          aria-label={`View ${s.name}'s progress`}
                          title="View progress"
                        >
                          <Icon name="LineChart" size={16} />
                        </button>
                        <button
                          onClick={() => setToDelete(s)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                          aria-label={`Remove ${s.name}`}
                          title="Remove"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </Button>
            <span className="px-1">
              Page {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              iconPosition="right"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ── Add-student modal ── */}
      <Modal
        open={addOpen}
        onClose={() => (saving ? null : setAddOpen(false))}
        title="Add student"
        description="Add one student to a batch."
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submitAdd} loading={saving} iconName="Check">
              Add student
            </Button>
          </>
        }
      >
        <form onSubmit={submitAdd} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Batch <span className="text-destructive">*</span>
            </label>
            <select
              value={form.placementSessionId}
              onChange={setField("placementSessionId")}
              className={selectClasses}
            >
              <option value="" disabled>
                Select a batch
              </option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Full name" required value={form.name} onChange={setField("name")} autoFocus />
            <Input label="Email" type="email" required value={form.email} onChange={setField("email")} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Roll number" value={form.rollNumber} onChange={setField("rollNumber")} />
            <Input label="Branch" value={form.branch} onChange={setField("branch")} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="CGPA"
              type="number"
              step="0.01"
              min="0"
              max="10"
              value={form.cgpa}
              onChange={setField("cgpa")}
            />
            <Input
              label="Graduation year"
              type="number"
              min="1950"
              max="2100"
              value={form.graduationYear}
              onChange={setField("graduationYear")}
            />
          </div>
          {formError && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={14} /> {formError}
            </p>
          )}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      </Modal>

      {/* ── Import modal ── */}
      <Modal
        open={importOpen}
        onClose={() => (importing ? null : setImportOpen(false))}
        title="Import students from CSV"
        description="Upload a roster — we'll match columns automatically."
        size="lg"
        footer={
          importResult ? (
            <Button onClick={() => setImportOpen(false)} iconName="Check">
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setImportOpen(false)} disabled={importing}>
                Cancel
              </Button>
              <Button
                onClick={submitImport}
                loading={importing}
                iconName="Upload"
                disabled={parsed.length === 0 || !importBatch}
              >
                Import {parsed.length > 0 ? `${parsed.length}` : ""} students
              </Button>
            </>
          )
        }
      >
        {importResult ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-success/30 bg-success/10 px-4 py-3">
              <Icon name="CheckCircle2" size={20} className="text-success" />
              <div className="text-sm">
                <div className="font-medium text-foreground">
                  Imported {importResult.inserted} of {importResult.received} rows
                </div>
                <div className="text-muted-foreground">
                  {importResult.skippedDuplicate} duplicate
                  {importResult.skippedDuplicate === 1 ? "" : "s"} skipped
                  {importResult.invalid?.length ? `, ${importResult.invalid.length} invalid` : ""}.
                </div>
              </div>
            </div>
            {importResult.invalid?.length > 0 && (
              <div className="rounded-lg border border-border">
                <div className="border-b border-border bg-muted/40 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Rows that need fixing
                </div>
                <ul className="max-h-52 divide-y divide-border overflow-y-auto text-sm">
                  {importResult.invalid.map((r, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 px-4 py-2">
                      <span className="truncate text-foreground">
                        Row {r.row}
                        {r.email ? ` · ${r.email}` : ""}
                      </span>
                      <span className="flex-shrink-0 text-destructive">{r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Import into batch <span className="text-destructive">*</span>
              </label>
              <select
                value={importBatch}
                onChange={(e) => setImportBatch(e.target.value)}
                className={selectClasses}
              >
                <option value="" disabled>
                  Select a batch
                </option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-background px-6 py-8 text-center transition-colors hover:bg-muted/40">
              <Icon name="FileUp" size={26} className="text-muted-foreground" />
              <span className="mt-2 text-sm font-medium text-foreground">
                {fileName || "Choose a CSV file"}
              </span>
              <span className="mt-0.5 text-xs text-muted-foreground">
                Columns: name, email, roll_number, branch, cgpa, graduation_year
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={onFile}
              />
            </label>

            {parsed.length > 0 && !parseError && (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-4 py-2.5 text-sm text-foreground">
                <Icon name="ListChecks" size={16} className="text-primary" />
                <span>
                  <span className="font-medium">{parsed.length}</span> valid row
                  {parsed.length === 1 ? "" : "s"} ready to import. Duplicates are skipped
                  automatically.
                </span>
              </div>
            )}
            {parseError && (
              <p className="flex items-center gap-1.5 text-sm text-destructive">
                <Icon name="AlertCircle" size={14} /> {parseError}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ── Delete confirm ── */}
      <Modal
        open={!!toDelete}
        onClose={() => (deleting ? null : setToDelete(null))}
        title="Remove student"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setToDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete} loading={deleting} iconName="Trash2">
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Remove <span className="font-medium text-foreground">{toDelete?.name}</span> from this
          batch? This also removes any interview assignments for them. This can&apos;t be undone.
        </p>
      </Modal>
    </div>
  );
}
